import { memo, useCallback, useMemo, useRef, useState } from "react";

import { Box, Stack } from "@mui/material";

import EmoticonPicker from "@/components/TextField/EmoticonPicker";
import emoticons from "@/components/TextField/emoticons";

import { Editable } from "@/extensions/obituary/pages/detail/Story/shared";
import { Emoticon } from "@/extensions/obituary/types";

import { useStory } from "../context";

import { Container as ContainerInterface } from "./Container";
import Singleline, {
  isSingleline,
  makeSingleline,
  Singleline as SinglelineInterface,
  SinglelineVariantButton,
} from "./Singleline";

export function makeRelativesColumn(
  firstSingleLineChild: Partial<SinglelineInterface>,
): ContainerInterface {
  return {
    id: crypto.randomUUID(),
    type: "container",
    variant: "relative,relatives-column",
    flex: "column",
    gap: true,
    children: [
      makeSingleline({
        variant: "mourners",
        type: "singleline",
        text: "",
        id: crypto.randomUUID(),
        ...firstSingleLineChild,
      }),
    ],
  } satisfies ContainerInterface;
}

interface TableLayoutMeta {
  columnIndex: number;
  columnCount: number;
  columnRowCounts: number[];
}

interface ExtraColumnProps {
  onDeleteSelf?: () => void;
  tableLayout?: TableLayoutMeta;
}

type ColumnProps = Parameters<Editable<ContainerInterface>>[0] &
  ExtraColumnProps;

const Edit: React.FC<ColumnProps> = ({
  item,
  path,
  onDeleteSelf,
  tableLayout,
}) => {
  const { children } = item;
  const { change } = useStory();
  const emoticonButtonRefs = useRef<Record<number, HTMLButtonElement | null>>(
    {},
  );
  const [openEmoticonPicker, setOpenEmoticonPicker] = useState(false);
  const [activeChildIndex, setActiveChildIndex] = useState<number | null>(null);
  const actionButtonSx = useMemo(
    () => ({
      backgroundColor: "background.paper",
      border: "1px solid",
      borderColor: "grey.400",
      width: 28,
      height: 28,
      p: 0,
      "&:hover": {
        backgroundColor: "background.paper",
        borderColor: "text.primary",
      },
    }),
    [],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newChildren = children.toSpliced(index, 1);
      if (newChildren.length === 0) {
        // If we deleted the last child, remove the entire column
        onDeleteSelf?.();
        return;
      }
      change<ContainerInterface>(path, {
        children: newChildren,
      });
    },
    [children, change, path, item.id],
  );

  const handleOpenEmoticonPicker = useCallback((index: number) => {
    setOpenEmoticonPicker(true);
    setActiveChildIndex(index);
  }, []);

  const handleCloseEmoticonPicker = useCallback(() => {
    setOpenEmoticonPicker(false);
    setActiveChildIndex(null);
  }, []);

  const handleSelectEmoticon = useCallback(
    (emoticon: Emoticon) => {
      if (activeChildIndex === null) return;
      const child = children[activeChildIndex];
      if (!child || !isSingleline(child)) return;

      change<SinglelineInterface>([...path, "children", activeChildIndex], {
        text: (child.text || "") + emoticon.code,
      });
      handleCloseEmoticonPicker();
    },
    [activeChildIndex, children, change, path, handleCloseEmoticonPicker],
  );

  const activeButtonRef = useRef<HTMLElement | null>(null);
  if (activeChildIndex !== null) {
    activeButtonRef.current =
      emoticonButtonRefs.current[activeChildIndex] ?? null;
  }

  const renderedChildren = useMemo(
    () =>
      children?.map((child, i) =>
        isSingleline(child) ? (
          <Box
            key={"column-" + child.id}
            sx={{
              position: "relative",
              "& .singleline-action-row": {
                position: "absolute",
                right: 8,
                top: 0,
                display: "flex",
                alignItems: "center",
                height: "100%",
                gap: 0.5,
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 100ms",
                zIndex: 1,
              },
              "&:hover .singleline-action-row": {
                opacity: 1,
                pointerEvents: "auto",
              },
            }}
          >
            <Singleline
              hideVariantButton
              noHeader
              item={child}
              path={[...path, "children", i]}
              tableCell={
                tableLayout != null
                  ? {
                      columnIndex: tableLayout.columnIndex,
                      columnCount: tableLayout.columnCount,
                      rowIndex: i,
                      rowCount: children.length,
                      hasLeftNeighbor:
                        tableLayout.columnIndex > 0 &&
                        (tableLayout.columnRowCounts[
                          tableLayout.columnIndex - 1
                        ] ?? 0) > i,
                    }
                  : undefined
              }
            />

            <Box className="singleline-action-row">
              <SinglelineVariantButton
                className="singleline-action-button"
                handleDelete={() => handleDelete(i)}
                hidden={child.hidden}
                item={child}
                path={[...path, "children", i]}
                tooltipPlacement="top"
              />
            </Box>
          </Box>
        ) : null,
      ),
    [
      children,
      path,
      handleDelete,
      handleOpenEmoticonPicker,
      actionButtonSx,
      onDeleteSelf,
      tableLayout,
    ],
  );

  return (
    <>
      <Stack direction="column" gap={tableLayout != null ? 0 : 1} width="100%">
        {renderedChildren}
      </Stack>

      <EmoticonPicker
        anchor={activeButtonRef as React.RefObject<HTMLElement>}
        emoticons={emoticons}
        open={openEmoticonPicker}
        placement="bottom"
        onClickAway={handleCloseEmoticonPicker}
        onEmoticonClick={handleSelectEmoticon}
      />
    </>
  );
};

export default memo(Edit);
